<template>
    <div class="quotation-history-page">
        <app-sub-title text="Historial de Cotización" />
        
        <div class="quotation-info mb-4">
            <div class="card">
                <div class="card-body">
                    <h5>{{ quotation.title || 'Cotización' }}</h5>
                    <p class="text-muted mb-0" v-if="quotation.client">
                        Cliente: <strong>{{ quotation.client.name }}</strong>
                    </p>
                    <p class="text-muted mb-0" v-if="quotation.code">
                        <strong>Código actual:</strong> <span class="badge badge-primary">{{ quotation.code }}</span>
                    </p>
                </div>
            </div>
        </div>

        <div class="history-list" v-if="!loading">
            <div v-if="logs.length > 0">
                <div
                    v-for="(log, index) in logs"
                    :key="index"
                    class="history-item mb-3 p-3 border rounded"
                >
                    <div class="row">
                        <div class="col-md-8">
                            <h6>
                                <i :class="getActionIcon(log.action)"></i>
                                {{ getActionLabel(log.action) }}
                            </h6>
                            <p class="text-muted small mb-1">
                                <strong>Usuario:</strong> {{ log.user ? log.user.name : 'N/A' }}
                            </p>
                            <p class="text-muted small mb-1" v-if="log.description">
                                {{ log.description }}
                            </p>
                            <div v-if="log.changes && log.changes.quotation_code" class="code-change mt-2 mb-2">
                                <strong>Código:</strong>
                                <span v-if="typeof log.changes.quotation_code === 'object' && log.changes.quotation_code.old" class="badge badge-secondary mr-2">
                                    {{ log.changes.quotation_code.old }}
                                </span>
                                <i class="fa fa-arrow-right mx-2" v-if="typeof log.changes.quotation_code === 'object' && log.changes.quotation_code.old"></i>
                                <span class="badge badge-primary">
                                    {{ typeof log.changes.quotation_code === 'object' ? (log.changes.quotation_code.new || log.changes.quotation_code) : log.changes.quotation_code }}
                                </span>
                            </div>
                            <div v-if="log.previous_status || log.new_status" class="status-change mt-2">
                                <strong>Estado:</strong>
                                <span v-if="log.previous_status" class="badge badge-secondary mr-2">
                                    {{ getStatusLabel(log.previous_status) }}
                                </span>
                                <i class="fa fa-arrow-right mx-2"></i>
                                <span v-if="log.new_status" class="badge badge-primary">
                                    {{ getStatusLabel(log.new_status) }}
                                </span>
                            </div>
                            <div v-if="log.changes && Object.keys(log.changes).length > 0" class="changes mt-2">
                                <div v-for="(change, field) in getFieldChanges(log.changes)" :key="field" class="change-item mb-2">
                                    <strong>{{ getFieldLabel(field) }}:</strong>
                                    <div class="change-values mt-1">
                                        <span class="badge badge-secondary mr-2">
                                            {{ formatChangeValue(change.old) }}
                                        </span>
                                        <i class="fa fa-arrow-right mx-2"></i>
                                        <span class="badge badge-success">
                                            {{ formatChangeValue(change.new) }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4 text-right">
                            <p class="text-muted small mb-0">
                                <strong>Fecha:</strong><br>
                                {{ formatDate(log.created_at) }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-center text-muted p-4">
                <p>No hay historial disponible para esta cotización</p>
            </div>
        </div>
        <div v-else class="text-center p-4">
            <p>Cargando historial...</p>
        </div>

        <div class="form-actions mt-4">
            <button class="btn btn-secondary" @click="goBack">
                <i class="fa fa-arrow-left"></i> Volver
            </button>
        </div>
    </div>
</template>

<script>
import SubTitle from '../../UI/Layout/SubTitle.vue';
import quotationsRepository from '../../../repositories/quotations.repository';

export default {
    components: {
        appSubTitle: SubTitle
    },
    data() {
        return {
            quotation: {
                title: '',
                client: null,
                code: null
            },
            logs: [],
            loading: true
        };
    },
    created() {
        this.loadQuotation();
        this.loadLogs();
    },
    methods: {
        loadQuotation() {
            const id = this.$route.params.id;
            if (!id) {
                toastr.error('ID de cotización no válido');
                this.goBack();
                return;
            }

            const pr = { ...quotationsRepository };
            pr.getQuotation(id)
                .then(response => {
                    if (response) {
                        this.quotation = {
                            title: response.title || 'Cotización',
                            client: response.client,
                            code: response.code || null
                        };
                    }
                })
                .catch(error => {
                    console.error('Error al cargar la cotización:', error);
                });
        },
        loadLogs() {
            const id = this.$route.params.id;
            if (!id) {
                this.goBack();
                return;
            }

            this.loading = true;
            const pr = { ...quotationsRepository };
            pr.getQuotationLogs(id)
                .then(response => {
                    if (response && Array.isArray(response)) {
                        this.logs = response.sort((a, b) => {
                            return new Date(b.created_at) - new Date(a.created_at);
                        });
                    } else {
                        this.logs = [];
                    }
                    this.loading = false;
                })
                .catch(error => {
                    toastr.error('Error al cargar el historial');
                    console.error(error);
                    this.logs = [];
                    this.loading = false;
                });
        },
        getActionLabel(action) {
            const actionMap = {
                'created': 'Cotización Creada',
                'updated': 'Cotización Actualizada',
                'status_changed': 'Estado Cambiado',
                'deleted': 'Cotización Eliminada'
            };
            return actionMap[action] || action;
        },
        getActionIcon(action) {
            const iconMap = {
                'created': 'fa fa-plus-circle text-success',
                'updated': 'fa fa-edit text-info',
                'status_changed': 'fa fa-exchange-alt text-warning',
                'deleted': 'fa fa-trash text-danger'
            };
            return iconMap[action] || 'fa fa-circle';
        },
        getStatusLabel(status) {
            const statusMap = {
                'draft': 'Borrador',
                'sent': 'Enviada',
                'approved': 'Aprobada',
                'rejected': 'Rechazada',
                'canceled': 'Cancelada'
            };
            return statusMap[status] || status;
        },
        getFieldChanges(changes) {
            if (!changes || typeof changes !== 'object') return {};
            
            const filtered = {};
            Object.keys(changes).forEach(key => {
                if (key !== 'quotation_code' && changes[key] && typeof changes[key] === 'object' && 'old' in changes[key] && 'new' in changes[key]) {
                    filtered[key] = changes[key];
                }
            });
            return filtered;
        },
        getFieldLabel(field) {
            const labels = {
                'title': 'Título',
                'description': 'Descripción',
                'status': 'Estado',
                'discount': 'Descuento',
                'total_usd': 'Total USD',
                'total_cop': 'Total COP',
                'total_cost': 'Costo Total',
                'installation_hours': 'Horas de Instalación',
                'trm_used': 'TRM Usado',
                'code': 'Código'
            };
            return labels[field] || field;
        },
        formatChangeValue(value) {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'number') {
                if (value % 1 !== 0) {
                    return new Intl.NumberFormat('es-CO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }).format(value);
                }
                return new Intl.NumberFormat('es-CO').format(value);
            }
            if (typeof value === 'string' && value.length > 50) {
                return value.substring(0, 50) + '...';
            }
            return String(value);
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            const date = new Date(dateString);
            return date.toLocaleString('es-CO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        goBack() {
            this.$router.push({ name: 'quotationsPage' });
        }
    }
};
</script>

<style scoped>
.quotation-history-page {
    padding: 20px;
}

.quotation-info {
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.history-item {
    background: #f8f9fa;
}

.status-change {
    display: flex;
    align-items: center;
}

.changes {
    margin-top: 10px;
}

.change-item {
    background: #f8f9fa;
    padding: 10px;
    border-radius: 4px;
    border-left: 3px solid #007bff;
}

.change-values {
    display: flex;
    align-items: center;
    margin-top: 5px;
}

.code-change {
    background: #e7f3ff;
    padding: 8px 12px;
    border-radius: 4px;
    border-left: 3px solid #007bff;
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
</style>

